import { DatabaseType } from "~/gen/api/backup"
import { FieldResponseDto } from "~/gen/api/encrypt"


export type DataTypeMaskingKind =
  | 'string'
  | 'integer_num'
  | 'non_integer_num'
  | 'date'
  | 'time'
  | 'not_support'

const StringMaskingDataTypes = 
  ['char', 'character', 'character varying', 'nchar', 'ntext', 'nvarchar', 'text', 'tinytext', 'mediumtext', 'longtext', 'varchar', 'varchar2', 'nvarchar2', 'clob', 'nclob',
    'graphic', 'vargraphic', 'dbclob']
  .reduce((acc, s) => {
    acc[s] = true
    return acc
  }, {} as Record<string, boolean>)
  
const IntegerNumMaskingDataTypes = 
  ['bigint', 'bigserial', 'int', 'integer', 'mediumint', 'serial', 'smallint', 'smallserial', 'tinyint', 'year']
  .reduce((acc, s) => {
    acc[s] = true
    return acc
  }, {} as Record<string, boolean>)

const NonIntegerNumMaskingDataTypes = 
  ['dec', 'decimal', 'double', 'double precision', 'fixed', 'float', 'money', 'numeric', 'real', 'smallmoney', 'number', 'binary_float', 'binary_double',
    'decfloat']
  .reduce((acc, s) => {
    acc[s] = true
    return acc
  }, {} as Record<string, boolean>)
  
const DateMaskingDataTypes =
  ['date', 'datetime', 'datetime2', 'datetimeoffset', 'smalldatetime', 
   'timestamp', 'timestamp with time zone', 'timestamp without time zone',
   'timestamp_ntz', 'timestamp_ltz', 'timestamp_tz']
  .reduce((acc, s) => {
    acc[s] = true
    return acc
  }, {} as Record<string, boolean>)
  
const TimeMaskingDataTypes =
  ['time', 'time with time zone', 'time without time zone']
  .reduce((acc, s) => {
    acc[s] = true
    return acc
  }, {} as Record<string, boolean>)
  
export const decideDataTypeMaskingKind = (dataType: string): DataTypeMaskingKind => {
  const dt = (dataType || '').toLowerCase()
  if (StringMaskingDataTypes[dt])  return 'string'
  if (IntegerNumMaskingDataTypes[dt])    return 'integer_num'
  if (NonIntegerNumMaskingDataTypes[dt]) return 'non_integer_num'
  if (TimeMaskingDataTypes[dt])    return 'time'
  if (DateMaskingDataTypes[dt] 
  || dt.startsWith('timestamp('))  return 'date'
  return 'not_support'
}


const strNumCmp = (a:string, b:string) => {
  const an = a.startsWith('-') 
  const bn = b.startsWith('-')
  if ((an as any) ^ (bn as any)) {
    return an ? -1 : 1
  } else {
    const absa = a.replace('-', '')
    const absb = b.replace('-', '')
    return absa === absb ? 0
    : ((absa < absb) as any) ^ (an as any) ? -1 : 1
  }
}

const bigIntegerLiteralRangeCheck = (numberStr:string, range: [string, string]) => {
  const absNum = numberStr.replace('-', '')
  if (absNum.length > 19) { return false }

  const [minv, maxv] = range
  const padded = absNum.padStart(19, '0')
  const paddedWithSign = numberStr.startsWith('-') ? '-' + padded : padded
  return strNumCmp(paddedWithSign, minv) >= 0 && strNumCmp(paddedWithSign, maxv) <= 0
}

export const integerRangeCheck = (numberStr:string, datatype: string) => {
  let range = [0, -1]
  switch (datatype.toLowerCase()) {
    case 'bigint':    { return bigIntegerLiteralRangeCheck(numberStr, ['-9223372036854775808', '9223372036854775807']) }
    case 'bigserial': { return bigIntegerLiteralRangeCheck(numberStr, ['0000000000000000001', '9223372036854775807']) }
    case 'int':       { range = [-2147483648, 2147483647]; break; }
    case 'integer':   { range = [-2147483648, 2147483647]; break; }
    case 'mediumint': { range = [-8388608, 8388607]; break; }
    case 'serial':    { range = [1, 2147483647]; break; }
    case 'smallint':  { range = [-32768, 32767]; break; }
    case 'smallserial': { range = [1, 32767]; break; }
    case 'tinyint':   { range = [0, 127]; break; } // intersect MySql ([-128, 127]) & MSSQL ([0, 255]) range
    case 'year':      { range = [1901, 2155]; if (numberStr === '0000') { return true }; break; }
    default: return false;
  }
  const [minv, maxv] = range
  const num = Number.parseInt(numberStr)
  return num >= minv && num <= maxv
}

export const nonIntegerRangeCheck = (numberStr:string, datatype: string, precision0?: number | null, scale0?: number | null) => {
  switch (datatype.toLowerCase()) {
    case 'number': { // Oracle
      const prop = scanNumStr(numberStr)
      const precision = !!precision0 ? precision0 : 38 // 38: Oracle - decimal max precision
      const scale = !scale0 ? (!!precision0 ? 0 : undefined) : scale0

      if (scale === undefined) { // NUMBER(null, null): both precision and scale are unspecifined
        // only check with max precision, allow arbitrary scale
        return prop.rightnz - prop.leftnz - (prop.rightnz * prop.leftnz >= 0 ? 1 : 0) <= 38
      } else if (scale === 0) { 
        return prop.wpLen <= precision 
      } else if (scale > 0) {
        if (scale > precision) {
          return - 1 - prop.leftnz >= scale - precision && prop.dpLen <= scale
        } else {
          return prop.wpLen <= precision - scale && prop.dpLen <= scale
        }
      } else if (scale < 0) {
        return prop.wpLen - prop.rightnz + 1 <= precision &&
         prop.rightnz - 1 >= Math.abs(scale)
      }
      return false
    }
    case 'fixed':
    case 'dec':
    case 'decimal':
    case 'numeric': {
      const prop = scanNumStr(numberStr)
      const precision = !precision0 || precision0 === -1 ? 38 : precision0 // 38: MSSQL - decimal max precision
      const scale = !scale0 || scale0 === -1 ? 0 : scale0
      return prop.wpLen <= precision - scale && prop.dpLen <= scale
    }
    case 'binary_double':
    case 'double':
    case 'double precision': { return numberStr.replace('-', '').replace('.', '').length <= 15 }
    case 'binary_float':
    case 'real': { return numberStr.replace('-', '').replace('.', '').length <= 7 }
    case 'float': { 
      const precision = 
        !precision0 || precision0 === -1 ? 7 
        : (precision0 < 25) ? 7 : 15
      return numberStr.replace('-', '').replace('.', '').length <= precision
    }
    case 'money': { // MSSQL - money
      if (numberStr.indexOf('.') < 0 || (numberStr.length - numberStr.indexOf('.') - 1) !== 4) { return false }
      const digits = numberStr.replace('-', '').replace('.', '')
      if (digits.length > 19) { return false }

      const str = (numberStr.indexOf('-') > -1 ? '-' : '') + digits.padStart(19, '0')
      return strNumCmp(str, '-9223372036854775808') >= 0 && strNumCmp(str, '9223372036854775807') <= 0
    }
    case 'smallmoney': { 
      if (numberStr.indexOf('.') < 0 || (numberStr.length - numberStr.indexOf('.') - 1) !== 4) { return false }

      const num = Number.parseInt(numberStr.replace('.', ''))
      return -2147483648 <= num && num <= 2147483647
    }
    case 'decfloat': { // DB2
      const digits = numberStr.replace('-', '').replace('.', '')
      const precision = (precision0 ?? 0) <=16 ? 16 : 34
      return digits.length <= precision
    }
    default: return false;
  }
}


export const getNumericTypeStorageLength = (x: FieldResponseDto) => {
  if (!x.type) { return }
  const type = x.type.toLowerCase()
  switch(type) {
    case 'bigint':
    case 'bigserial': { return 8 }
    case 'int':
    case 'serial':
    case 'integer': { return 4 }
    case 'mediumint': { return 3 }
    case 'smallint': 
    case 'smallserial': { return 2 }
    case 'year': 
    case 'tinyint': { return 1 }
    case 'number': 
    case 'fixed':
    case 'dec':
    case 'decimal':
    case 'numeric': {
      const p = !x.precision || x.precision === -1 ? 38 : x.precision 
      return p < 10 ? 5
        : p < 20 ? 9
        : p < 29 ? 13
        : 17
    }
    case 'binary_double':
    case 'double':
    case 'double precision': { return 8 }
    case 'binary_float':
    case 'real': { return 4 }
    case 'float': {
      const p = 
        !x.precision || x.precision === -1 ? 7 
        : (x.precision < 25) ? 7 : 15
      return p === 7 ? 4 : 8
    }
    case 'money': { return 8 }
    case 'smallmoney': { return 4 }
    default: { return 0 }
  }
}

export const dataTypeDisplayInfo = (d: { 
  dataType?:string|null, 
  maxLength?:number|null, 
  precision?:number|null, 
  scale?:number|null 
}, dbType?: DatabaseType) => {
  const dataType = d.dataType || ''
  const maskingKind = decideDataTypeMaskingKind(dataType)
  let dataTypeExt = null
  switch(maskingKind) {
    case 'string': { 
      if (dataType.toLocaleLowerCase().endsWith('text') && dbType !== DatabaseType.Snowflake) { break; }
      if (d.maxLength && d.maxLength > 0) {
        const maxLength = d.maxLength
        const factor = dataType.startsWith('n') || dataType.startsWith('N')
          ? 2 : 1
        dataTypeExt = `(${Math.floor(maxLength / factor)})`
      }
      break; 
    }
    case 'non_integer_num': { 
      if (d.precision) {
        dataTypeExt = `(${d.precision}${d.scale ? `, ${d.scale}` : ''})`
      }
      break;
    }
    case 'date': {
      if (dbType === DatabaseType.Snowflake && d.precision) {
        dataTypeExt = `(${d.precision})`
      }
      break;
    }
    default: break;
  }
  return { dataType, dataTypeExt }
}

interface NumStrProp {
  wpLen: number   // length of whole number part; positive
  dpLen: number   // length of decimal part; positive
  leftnz: number  // left none zero digit position relative to decimal point
                  // positive or negative
  rightnz: number // right none zero digit position relative to decimal point
                  // positive or negative
}

const scanNumStr = (numberStr: string) => {
  const numberStr1 = numberStr.trim().replace('-', '')
  const i = numberStr1.indexOf('.')
  const prop: NumStrProp = {} as any
  if (i === -1) {
    prop.wpLen = numberStr1.length
    prop.dpLen = 0
  } else {
    prop.wpLen = i
    prop.dpLen = numberStr1.length - i - 1
  }
  prop.leftnz = numberStr1.length - i - 1
  prop.rightnz = numberStr1.length - i - 1
  for(const ci in (numberStr1 as any)) {
    const cii = +ci
    const c = numberStr1[cii]
    if (c !== '0' && c !== '.') {
      prop.leftnz = (i === -1 ? numberStr.length : i) - cii
      break;
    }
  }
  for(const ci in (numberStr1 as any)) {
    const cii = numberStr1.length - (1 + Number(ci))
    const c = numberStr1[cii]
    if (c !== '0' && c !== '.') {
      prop.rightnz = (i === -1 ? numberStr.length : i) - cii
      break;
    }
  }
  return prop
}


export type NumericSubKind =
| 'fix_point_num'
| 'float_point_num'
| 'money'

const FixPointNumDataTypes =
  ['dec', 'decimal', 'fixed', 'numeric', 'number', 'decfloat']
  .reduce((acc, s) => {
    acc[s] = true
    return acc
  }, {} as Record<string, boolean>)

const FloatPointNumDataTypes =
  ['double', 'double precision', 'float', 'real', 'binary_float', 'binary_double']
  .reduce((acc, s) => {
    acc[s] = true
    return acc
  }, {} as Record<string, boolean>)

const MoneyNumDataTypes =
  ['money', 'smallmoney']
  .reduce((acc, s) => {
      acc[s] = true
      return acc
    }, {} as Record<string, boolean>)

export const figureOutNumericSubKind = (dataType:string): NumericSubKind|undefined => {
  const mk = decideDataTypeMaskingKind(dataType)
  if (mk !== 'non_integer_num') { return }

  const dt = (dataType || '').toLocaleLowerCase()
  if (FixPointNumDataTypes[dt]) return 'fix_point_num'
  if (FloatPointNumDataTypes[dt]) return 'float_point_num'
  if (MoneyNumDataTypes[dt]) return 'money'
}

export const isSinglePrecisionFloatPoint = (x: string) => {
  const x_low = x.toLocaleLowerCase()
  return x_low === 'real' || x_low === 'binary_float'
}
